/**
 * web/src/lib/approvalsDerive.ts
 * Pure derivation helpers for the Speaker Approvals surface (no React). Counts,
 * oldest-wait, filtering, sorting, grouping, and the selection mix are all
 * derived per render from the pending-gate list — never stored (design
 * handoff §Data model). Unit-tested in isolation.
 */
import { APPROVAL_KINDS, KIND_BY_ID, type ApprovalKind } from './approvalKinds';
import type { PendingGate } from '../api/gates';

/** A pending gate joined with its kind + roster context for one list row. */
export interface ApprovalRow {
  gate: PendingGate;
  kind: ApprovalKind;
  /** Speaker display name (workflow.displayName). */
  speaker: string;
  /** From the roster join — null when the roster has no matching row. */
  company: string | null;
  jobTitle: string | null;
  returning: boolean;
}

export type ApprovalSort = 'oldest' | 'newest' | 'name';

/** Stable per-gate key used for selection sets and queue outcomes. */
export function gateKey(g: { workflowId: string; stepIndex: number }): string {
  return `${g.workflowId}:${g.stepIndex}`;
}

/** Whole days a gate has been waiting (floor; never negative). */
export function daysWaiting(createdAt: string, now: Date): number {
  const ms = now.getTime() - new Date(createdAt).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/** ≥7 days waiting gets the overdue treatment (design §Row). */
export const OVERDUE_DAYS = 7;

/** Builds rows from pending gates: attach kind + roster context. Gates whose
 *  stepId has no registry entry are dropped (nothing renderable to say). */
export function buildRows(
  gates: PendingGate[],
  rosterByWorkflowId: ReadonlyMap<
    string,
    { company: string | null; jobTitle: string | null; previousSpeaker: boolean }
  >,
): ApprovalRow[] {
  const rows: ApprovalRow[] = [];
  for (const gate of gates) {
    const kind = KIND_BY_ID.get(gate.stepId);
    if (!kind) continue;
    const roster = rosterByWorkflowId.get(gate.workflowId);
    rows.push({
      gate,
      kind,
      speaker: gate.workflow.displayName,
      company: roster?.company ?? null,
      jobTitle: roster?.jobTitle ?? null,
      returning: roster?.previousSpeaker ?? false,
    });
  }
  return rows;
}

/** Per-kind count + oldest wait for the strip cards (registry order). */
export function kindStats(
  rows: ApprovalRow[],
  now: Date,
): Map<string, { count: number; oldestDays: number }> {
  const stats = new Map<string, { count: number; oldestDays: number }>();
  for (const k of APPROVAL_KINDS) stats.set(k.id, { count: 0, oldestDays: 0 });
  for (const r of rows) {
    const s = stats.get(r.kind.id)!;
    s.count += 1;
    s.oldestDays = Math.max(s.oldestDays, daysWaiting(r.gate.createdAt, now));
  }
  return stats;
}

/** Multi-select kind filter (empty = all) + search on speaker name/company. */
export function filterRows(rows: ApprovalRow[], kindFilter: string[], query: string): ApprovalRow[] {
  const kinds = new Set(kindFilter);
  const q = query.trim().toLowerCase();
  return rows.filter((r) => {
    if (kinds.size > 0 && !kinds.has(r.kind.id)) return false;
    if (q === '') return true;
    return (
      r.speaker.toLowerCase().includes(q) ||
      (r.company ?? '').toLowerCase().includes(q) ||
      (r.jobTitle ?? '').toLowerCase().includes(q)
    );
  });
}

/** Sort: longest waiting (default) / newest first / speaker A–Z. */
export function sortRows(rows: ApprovalRow[], sort: ApprovalSort): ApprovalRow[] {
  const out = [...rows];
  if (sort === 'oldest') {
    out.sort((a, b) => a.gate.createdAt.localeCompare(b.gate.createdAt));
  } else if (sort === 'newest') {
    out.sort((a, b) => b.gate.createdAt.localeCompare(a.gate.createdAt));
  } else {
    out.sort((a, b) => a.speaker.localeCompare(b.speaker));
  }
  return out;
}

/**
 * Grouping collapses to a single flat list when the view is already narrowed
 * to one thing: exactly one kind filtered, a non-empty search, or sort = A–Z.
 */
export function isFlatView(kindFilter: string[], query: string, sort: ApprovalSort): boolean {
  return kindFilter.length === 1 || query.trim() !== '' || sort === 'name';
}

/** Groups shown rows by kind, in registry order; empty kinds omitted. */
export function groupRows(shown: ApprovalRow[]): { kind: ApprovalKind; rows: ApprovalRow[] }[] {
  const byKind = new Map<string, ApprovalRow[]>();
  for (const r of shown) {
    const arr = byKind.get(r.kind.id) ?? [];
    arr.push(r);
    byKind.set(r.kind.id, arr);
  }
  return APPROVAL_KINDS.filter((k) => byKind.has(k.id)).map((k) => ({
    kind: k,
    rows: byKind.get(k.id)!,
  }));
}

/**
 * The dock's mix line — `12 logistics email · 3 speaker card` — so a mixed
 * queue is never a surprise. Registry order; labels lowercased.
 */
export function selectionMix(selected: ApprovalRow[]): string {
  const counts = new Map<string, number>();
  for (const r of selected) counts.set(r.kind.id, (counts.get(r.kind.id) ?? 0) + 1);
  return APPROVAL_KINDS.filter((k) => counts.has(k.id))
    .map((k) => `${counts.get(k.id)} ${k.label.toLowerCase()}`)
    .join(' · ');
}
